import {
    ChangeEvent,
    FC,
    MouseEvent,
    ReactElement,
    useState,
} from 'react'
import { Navigate } from 'react-router'
import classNames from 'classnames'

import { Button, Card, Logo } from '@/components'
import { Credentials } from '@/helpers/api/authenticationApi'
import { trans } from '@/helpers/trans'
import { authenticateUser, forgotUserPassword } from '@/redux/authentication/authenticationActions'
import { useTypedDispatch, useTypedSelector } from '@/redux/store'

import './LoginPage.scss'

const emailRegEx = /[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.)+[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?/

export const LoginPage: FC = (): ReactElement => {
    const dispatch = useTypedDispatch()
    const { loading, isAuthenticated, authenticateFailure } = useTypedSelector(state => state.authenticationReducer)
    const [credentials, setCredentials] = useState<Credentials>({ username: '', password: '' })
    const [email, setEmail] = useState('')
    const [emailCheck, setEmailCheck] = useState(true)
    const [forgotPassword, setForgotPassword] = useState(false)
    const [forgotPasswordRequested, setForgotPasswordRequested] = useState(false)

    const handleCredentialsChange = (event: ChangeEvent<HTMLInputElement>): void => {
        const { name, value } = event.currentTarget

        setCredentials({ ...credentials, [name]: value })
    }

    const handleLoginClick = (event: MouseEvent): void => {
        event.preventDefault()
        dispatch(authenticateUser(credentials))
    }

    const handleForgotPasswordClick = (): void => {
        setForgotPassword(true)
        setForgotPasswordRequested(false)
    }

    const handleEmailChange = (event: ChangeEvent<HTMLInputElement>): void => {
        setEmail(event.currentTarget.value)
    }

    const handleRequestNewPasswordClick = (event: MouseEvent): void => {
        event.preventDefault()
        const isEmailValid = email.search(emailRegEx) === 0

        setEmailCheck(isEmailValid)
        setForgotPasswordRequested(isEmailValid)

        if (isEmailValid) {
            dispatch(forgotUserPassword(email))
        }
    }

    const handleBackClick = (): void => {
        setForgotPassword(false)
    }

    if (isAuthenticated) {
        return <Navigate to={`/${trans('nav.shifts.link')}`} replace />
    }

    return (
        <div className="bg-primary vh-100 d-flex">
            <div className="login">
                <Logo className="login__logo" />
                <Card className="login__card">
                    {! forgotPassword ? (
                        <form className="login__form">
                            <div className="form-group">
                                <label className="text-primary" htmlFor="username">{trans('pages.login.username')}</label>
                                <input
                                    className="form-control"
                                    onChange={handleCredentialsChange}
                                    type="text"
                                    name="username"
                                    id="username"
                                    autoComplete="username"
                                    placeholder={trans('pages.login.username')}
                                />
                            </div>
                            <div className="form-group">
                                <label className="text-primary" htmlFor="password">{trans('pages.login.password')}</label>
                                <input
                                    className="form-control"
                                    onChange={handleCredentialsChange}
                                    type="password"
                                    name="password"
                                    id="password"
                                    autoComplete="current-password"
                                    placeholder={trans('pages.login.password')}
                                />
                            </div>
                            {authenticateFailure && <p className="text-danger">{trans('pages.login.loginError')}</p>}
                            <Button
                                text={trans('common.login')}
                                disabled={loading}
                                type="submit"
                                onClick={handleLoginClick}
                                className="login__submit-button"
                            />
                            <Button
                                text={trans('pages.login.forgotPassword')}
                                disabled={loading}
                                onClick={handleForgotPasswordClick}
                                className="login__forgot-password-button"
                            />
                        </form>
                    ) : (
                        <form className="login__form">
                            <h4 className="login__title font-weight-semi-bold">
                                {trans('pages.login.forgotPassword')}
                            </h4>
                            <p className="login__intro">
                                {trans('pages.login.forgotPasswordIntro')}
                            </p>
                            <div className="form-group forgot-password-form-group">
                                <input
                                    className={classNames('form-control', 'login__forgot-password-input', { 'is-invalid': ! emailCheck })}
                                    onChange={handleEmailChange}
                                    disabled={forgotPasswordRequested}
                                    type="text"
                                    name="email"
                                    id="email"
                                    placeholder={trans('common.email')}
                                />
                                <div className="invalid-feedback">
                                    {trans('pages.registration.emailNotValid')}
                                </div>
                                {forgotPasswordRequested && (
                                    <p>
                                        {trans('pages.login.forgotPasswordRequested')}
                                    </p>
                                )}
                                <Button
                                    text={trans('common.send')}
                                    type="submit"
                                    onClick={handleRequestNewPasswordClick}
                                    disabled={forgotPasswordRequested}
                                    className="login__request-new-password-button"
                                />
                                <Button
                                    text={trans('common.back')}
                                    onClick={handleBackClick}
                                    className="login__back-button"
                                />
                            </div>
                        </form>
                    )}
                </Card>
            </div>
        </div>
    )
}
